import { Injectable } from '@angular/core';
import { Observable, ReplaySubject, lastValueFrom, take } from 'rxjs';
import { StorageService } from '../storage/storage.service';
import { ActivatedRoute, Router } from '@angular/router';
import * as SentryAngular from '@sentry/angular';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { switchMap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AuthStatesEnum, Client, StrapiFile } from '../../models';
import { AlertController, NavController } from '@ionic/angular';
import { UserDetailsFormModel } from '../../models/userDetailsFormModel.model';
import { TranslocoService } from '@ngneat/transloco';
import { BiometricService } from '../biometric/biometric.service';
import { AuthModalService } from '../auth/auth-modal.service';
import { Capacitor } from '@capacitor/core';
import { PlatformEnum } from '../../models/enums/platform.enum';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class UserService {
  public onlineUser$: ReplaySubject<Client | undefined> = new ReplaySubject<Client | undefined>(0);
  private jwt?: string;

  constructor (
    private storage: StorageService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private httpClient: HttpClient,
    private navController: NavController,
    private transloco: TranslocoService,
    private alertController: AlertController,
    private biometricService: BiometricService,
    private authModal: AuthModalService
  ) {
  }

  public async init() {
    await this.loadUser();
  }

  public async loadUser() {
    const localUserStored = await this.storage.get('user');

    if (localUserStored) {
      const localUser: Client = JSON.parse(localUserStored);
      this.onlineUser$.next(localUser);

      if (Capacitor.getPlatform() === PlatformEnum.WEB) {
        this.jwt = await this.storage.get('token');
      }

      if (this.jwt) {
        try {
          const userData: Client = await lastValueFrom(this.onlineUser);
          this.onlineUser$.next(userData);
          this.saveLocalUser(userData);
        } catch (e: any) {
          return;
        }
      }
    } else {
      this.onlineUser$.next(undefined);
    }
  }

  get () {
    return this.onlineUser$.asObservable();
  }

  get token () {
    return this.jwt;
  }

  private get onlineUser () {
    return this.httpClient.get<Client>(`${environment.baseUrl}/users/me`);
  }

  authenticate (jwt: string, user: Client) {
    this.jwt = jwt;
    this.onlineUser$.next(user);
    if (Capacitor.getPlatform() === PlatformEnum.WEB) {
      this.storage.set('token', this.jwt)
    }
    return this.onlineUser.pipe(
      switchMap((userData: Client) => {
        this.biometricService.setBiometricInfo(userData);
        this.onlineUser$.next(userData);
        this.saveLocalUser(userData);
        return this.navController.navigateRoot('/');
      }),
      untilDestroyed(this)
    );
  }

  async logout (force = false) {
    const scope = SentryAngular.getCurrentScope();
    if (force) {
      console.error('User logged forced to log out')
    }
    this.storage.set('user', null);
    this.storage.set('token', null)
    scope.setUser(null);
    this.biometricService.deleteCredentials();
    this.biometricService.removeBiometricInfo();
    this.onlineUser$.next(undefined);
    this.jwt = undefined;
    return lastValueFrom(this.activatedRoute.queryParams.pipe(take(1))).then((queryParams) => {
      return this.router.navigate(['/auth'], {queryParams})
    });
  }

  async confirmLogout(): Promise<void> {
    const alert: HTMLIonAlertElement = await this.alertController.create({
      header: this.transloco.translate('USER.CONFIRM_LOGOUT'),
      buttons: [
        {
          text: this.transloco.translate('USER.LOGOUT'),
          role: 'destructive',
          handler: () => {
            this.logout().then(() => {
              this.authModal.closeLogin(AuthStatesEnum.BIOMETRIC_AUTH);
            });
          },
        },
        {
          text: this.transloco.translate('CANCEL'),
          role: 'cancel',
        }
      ],
    });
    await alert.present();
  }

  private saveLocalUser (userData?: Client) {
    if (userData) {
      const scope = SentryAngular.getCurrentScope();
      scope.setUser({
        id: userData.id,
        email: userData.email,
      });
      this.storage.set('user', JSON.stringify(userData));
    }
  }

  editInformationUser(editForm: UserDetailsFormModel, userId?: number): Observable<Client> {
    return this.onlineUser$.pipe(
      take(1),
      switchMap((userData) =>
        this.httpClient.put<Client>(`${environment.baseUrl}/users/${userId ?? userData?.id}`, editForm)
      ),
      untilDestroyed(this)
    );
  }
  
  uploadMedia(file: File): Observable<StrapiFile[]> {
    const formData: FormData = new FormData();
    formData.append('files', file);
  
    return this.httpClient
      .post<StrapiFile[]>(`${environment.baseUrl}/documents/uploadPicture`, formData).pipe(
        take(1)
      );
  }

  deleteMedia(fileId: number): Observable<StrapiFile> {
    return this.httpClient.delete<StrapiFile>(`${environment.baseUrl}/documents/delete/${fileId}`).pipe(
      take(1)
    );
  }
}

